/**
 * Application Entry Point (vite-react-ssg)
 *
 * `ViteReactSSG` builds the react-router data router from `routes` (see
 * `./app/router.tsx`) and returns a `createRoot` function used by:
 *
 * - `vite-react-ssg build` — to server-render each statically-included
 *   route (see `ssgOptions.includedRoutes` in `vite.config.ts`) to full
 *   HTML at build time.
 * - the browser — to hydrate that static HTML (marketing routes) or
 *   client-render from scratch (SPA-only routes) on load, and to drive
 *   client-side navigation afterwards. Same React tree either way — this
 *   file does not need an `if (isClient)` branch for basic usage.
 *
 * See docs/v2/06-frontend-geo.md for the SSG/GEO plan this implements.
 */
import { ViteReactSSG } from "vite-react-ssg";
import { checkAdminPasswordOrExit } from "./lib/config/adminPasswordCheck";
import { routes } from "./app/router";
import "./index.css";

// This check intentionally only runs in the browser (`typeof window !==
// "undefined"`), never during `vite-react-ssg build`'s Node-side static
// rendering pass. Marketing routes (the only routes that get pre-rendered,
// see `ssgOptions.includedRoutes`) require neither Supabase nor the admin
// password, and `main.tsx` is now evaluated once in Node at build time in
// addition to in the browser — without this guard, a missing/insecure env
// var would call `process.exit(1)` and abort the *entire* static build
// (including marketing pages that don't need these vars at all), not just
// fail fast for an interactive browser session as originally intended.
if (typeof window !== "undefined") {
  // Migrated (see "Migrate existing frontend environment variables to
  // runtime config format"): this used to also call
  // `validateEnvironmentOrExit()`, which hard-required
  // VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY / VITE_API_BASE_URL as
  // *build-time* env vars and threw (white-screening the app) if they were
  // absent. `supabaseUrl` / `supabaseAnonKey` / `apiBaseUrl` are now
  // supplied at runtime by `ConfigProvider` (see
  // docs/v2/05-runtime-configuration.md and
  // src/lib/config/runtimeConfig.ts), which fetches and validates
  // `/runtime-config.json` itself and falls back to a minimal config (with
  // a visible warning banner) if it can't load — so a deployment
  // configured purely through runtime-config.json (no VITE_* vars baked in
  // at build time) is fully supported and no longer blocked here. The
  // build must not depend on `VITE_SUPABASE_*` / `VITE_API_BASE_URL`
  // directly, so that call has been removed from the startup path.
  //
  // `src/lib/envValidation.ts` itself is left in place (and still tested)
  // as an optional, explicitly-invoked check for deployments that choose
  // to keep embedding these values at build time via a `.env` file, but it
  // is intentionally no longer wired into app bootstrap.

  // TODO(Migrate existing frontend environment variables to runtime config
  // format): `checkAdminPasswordOrExit()` still hard-requires
  // VITE_ADMIN_PASSWORD as a build-time env var (see
  // src/lib/config/adminPasswordCheck.ts) and will white-screen a
  // deployment that only provides configuration via runtime-config.json.
  // Admin password enforcement was implemented by a separate issue
  // ("Enforce removal of default ADMIN_PASSWORD...") and isn't part of the
  // `RuntimeConfig` schema; migrating it (e.g. adding an optional
  // `adminPassword` field to runtime config) is left for a follow-up so as
  // not to change security-gating behavior as a side effect of this issue.
  checkAdminPasswordOrExit();
}

export const createRoot = ViteReactSSG({ routes });
